import { Props as ApexChartOptions } from "react-apexcharts";
import { RRule, Weekday } from "rrule";

import AVATAR from "@/assets/images/avatar.jpg";
import { SelectOptions } from "@/component/input";
import { TypeMedication } from "@/interfaces/IServiceUser";
import { DayTime } from "@/interfaces/ITaskManagement";
import { IContacts } from "@/pages/staff/staff-members/add";
import { getPublicEnv } from "../runtime-utils/config/public-env";
import { QuestionType } from "@/interfaces/ICarePlanning";

type AlertCategory = {
  label: string;
  value: string;
};

export const getMuiLicenseKey = () => getPublicEnv("NEXT_PUBLIC_MUI_KEY");

export const MUI_LICENSE_KEY = getMuiLicenseKey();

export const DEFAULT_DATE_FORMAT = "DD/MM/YYYY";

export const UNALLOCATED = "Unassigned visits";

export const INVISIBLE_CHAR = "‎";

export const maxSizeMB = 10;
export const maxSizeBytes = maxSizeMB * 1024 * 1024;

export const LIMITS = {
  name: 15,
  text: 255, // Increased from 100 to 255 for medication name
  note: 500,
  phone: 13,
  smallNumber: 4,
  description: 10 * 1000,
  password: 25,
  postCode: 8,

  age: 3,
};

export enum ACCESS_LEVEL {
  USER = "user",

  SENIOR_USER = "senior-user",

  ADMINISTRATOR = "administrator",
}

export enum HANDOVER {
  OPEN = "open",

  STAFF = "staff",
}

// "active" | "in-active" | "respite" | "hospitalised" | "deceased" | "discharged"
export const ACTIVE_STATUSES = ["active"];

export const TEMP_AVATAR = AVATAR;

export const HANDOVER_OPTIONS: SelectOptions[] = [
  {
    label: "Pin to handover",
    value: HANDOVER.OPEN,
  },
  {
    label: "Pin to a staff member",
    value: HANDOVER.STAFF,
  },
];

export const MEDICATION_OUTCOMES = [
  {
    label: "Maybe taken",
    value: "MT",
  },
  {
    label: "Not observed",
    value: "NO",
  },
  {
    label: "Not taken",
    value: "NT",
  },
  {
    label: "Partially taken",
    value: "PT",
  },
  {
    label: "Fully taken",
    value: "FT",
  },
  {
    label: "Not reported",
    value: "NR",
  },
];

export const getMedicationReasons = (outcome: string) => {
  switch (outcome) {
    case "MT":
    case "NT":
    case "PT":
      return [
        {
          label: "Refused",
          value: "Refused",
        },
        {
          label: "Destroyed",
          value: "Destroyed",
        },
        {
          label: "Nausea and vomiting",
          value: "Nausea and vomiting",
        },
        {
          label: "Missing medication",
          value: "Missing medication",
        },
        {
          label: "Medication error",
          value: "Medication error",
        },
        {
          label: "Other",
          value: "Other",
        },
      ];
    case "NO":
      return [
        {
          label: "Family administered",
          value: "Family administered",
        },
        {
          label: "Hospitalised",
          value: "Hospitalised",
        },
        {
          label: "Self administered",
          value: "Self administered",
        },
        {
          label: "Prepared and left out",
          value: "Prepared and left out",
        },
        {
          label: "Other",
          value: "Other",
        },
      ];
    default:
      return [];
  }
};

export const medicationOutcomeRequiresReason = (outcome: string) =>
  getMedicationReasons(outcome).length > 0;

export const DAYTIME: {
  label: string;
  value: DayTime;
}[] = [
  {
    label: "Morning",
    value: "morning",
  },
  {
    label: "Lunch",
    value: "lunch",
  },
  {
    label: "Evening",
    value: "evening",
  },
  {
    label: "Night",
    value: "night",
  },
];

export const FREQUENCIES = [
  {
    label: "Daily",
    value: "daily",
  },
  {
    label: "Weekly",
    value: "weekly",
  },
  {
    label: "Bi-Weekly",
    value: "biweekly",
  },

  {
    label: "Monthly",
    value: "monthly",
  },
];

export const DAYS: {
  label: string;
  value: Weekday;
}[] = [
  {
    label: "Monday",
    value: RRule.MO,
  },
  {
    label: "Tuesday",
    value: RRule.TU,
  },
  {
    label: "Wednesday",
    value: RRule.WE,
  },
  {
    label: "Thursday",
    value: RRule.TH,
  },
  {
    label: "Friday",
    value: RRule.FR,
  },
  {
    label: "Saturday",
    value: RRule.SA,
  },
  {
    label: "Sunday",
    value: RRule.SU,
  },
];

export const about = [
  {
    id: "1",
    heading: "About what is important to me",
    title: "importantToMe",
  },
  {
    id: "2",
    heading: "About my preferred way to communicate with me",
    title: "communicate",
  },
  {
    id: "3",
    heading: "About my wellness",
    title: "wellness",
  },
  {
    id: "4",
    heading: "About my Likes and Dislikes",
    title: "LikesDislikes",
  },
];

export const MEDICATION_ROUTE_OPTIONS: SelectOptions[] = [
  {
    label: "Oral route",
    value: "Oral route",
  },
  {
    label: "Injection routes",
    value: "Injection routes",
  },
  {
    label: "Sublingual and buccal routes",
    value: "Sublingual and buccal routes",
  },
  {
    label: "Rectal route",
    value: "Rectal route",
  },
  {
    label: "Vaginal route",
    value: "Vaginal route",
  },
  {
    label: "Ocular route",
    value: "Ocular route",
  },
  {
    label: "Otic route",
    value: "Otic route",
  },
  {
    label: "Nasal route",
    value: "Nasal route",
  },
  {
    label: "Inhalation route",
    value: "Inhalation route",
  },
  {
    label: "Nebulization route",
    value: "Nebulization route",
  },
  {
    label: "Cutaneous route",
    value: "Cutaneous route",
  },
  {
    label: "Transdermal route",
    value: "Transdermal route",
  },
  {
    label: "Other",
    value: "Other",
  },
];

export const TITLES: SelectOptions[] = [
  { label: "Mr", value: "mr" },
  { label: "Mrs", value: "mrs" },
  { label: "Master", value: "master" },
  { label: "Miss", value: "miss" },
  { label: "Ms", value: "ms" },
  { label: "Sir", value: "sir" },
  { label: "Lady", value: "lady" },
  { label: "Lord", value: "lord" },
  { label: "Dame", value: "dame" },
  { label: "Dr.", value: "dr" },
  { label: "Prof.", value: "prof" },
];

export const JOB_TITLES: SelectOptions[] = [
  { label: "CEO", value: "ceo" },
  { label: "Registered manager", value: "registered_manager" },
  { label: "Owner", value: "owner" },
  { label: "Deputy manager", value: "deputy_manager" },
  { label: "Managing director", value: "managing_director" },
  { label: "Admin staff", value: "admin_staff" },
  { label: "Care coordinator", value: "care_coordinator" },
  { label: "Care home manager", value: "care_home_manager" },
  { label: "Care staff", value: "care_staff" },
  { label: "Clinical staff", value: "clinical_staff" },
  { label: "Compliance staff", value: "compliance_staff" },
  { label: "Nurse", value: "nurse" },
  { label: "Regional manager", value: "regional_manager" },
  { label: "Senior manager", value: "senior_manager" },
  { label: "Support worker", value: "support_worker" },
  { label: "Other", value: "other" },
];

export const ACCESS_DETAILS: SelectOptions[] = [
  {
    value: "user",
    label: "User (App only)",
  },
  {
    value: "senior-user",
    label: "Senior User",
  },
  {
    value: "administrator",
    label: "Administrator",
  },
];

export const SERVICE_USER_DOC_TYPES: SelectOptions[] = [
  { value: "Accident and Incident", label: "Accident and Incident" },
  { value: "Care Plans", label: "Care Plans" },
  { value: "Charts", label: "Charts" },
  { value: "Consent", label: "Consent" },
  { value: "Continence", label: "Continence" },
  {
    value: "Deprivation of Liberty safeguards",
    label: "Deprivation of Liberty safeguards",
  },
  { value: "Discharge", label: "Discharge" },
  { value: "DNACPR", label: "DNACPR" },
  { value: "Hospital discharge", label: "Hospital discharge" },
  { value: "Induction", label: "Induction" },
  { value: "Initial Assessment", label: "Initial Assessment" },
  { value: "Letters", label: "Letters" },
  { value: "Medical condition", label: "Medical condition" },
  { value: "Mental and social state", label: "Mental and social state" },
  { value: "Nutrition", label: "Nutrition" },
  { value: "Weight", label: "Weight" },
  { value: "Positive behaviour support", label: "Positive behaviour support" },
  { value: "Risk Assessments", label: "Risk Assessments" },
  { value: "Support plans", label: "Support plans" },
  {
    value: "Transfers and mobility handling",
    label: "Transfers and mobility handling",
  },
  { value: "Medication audits", label: "Medication audits" },
  { value: "Other", label: "Other" },
];

export const STAFF_DOC_TYPES: SelectOptions[] = [
  { value: "Employment documents", label: "Employment documents" },
  { value: "Training", label: "Training" },
  { value: "Supervision and Appraisal", label: "Supervision and Appraisal" },
  { value: "Onboarding", label: "Onboarding" },
  { value: "Other", label: "Other" },
];

export const GENERAL_DOC_TYPES: SelectOptions[] = [
  { value: "Policies and Procedures", label: "Policies and Procedures" },
  { value: "Employee handbook", label: "Employee handbook" },
  { value: "Other", label: "Other" },
];

export const USER_STATUS: SelectOptions[] = [
  {
    value: "active",
    label: "Active",
  },
  {
    value: "in-active",
    label: "Inactive",
  },
  { value: "respite", label: "Respite" },
  { label: "Hospitalised", value: "hospitalised" },
  { label: "Deceased", value: "deceased" },
  { label: "Discharged", value: "discharged" },
];
export const USER_ADMIN: SelectOptions[] = [
  {
    value: "",
    label: "",
  },
];

export const CONTACT_TYPE: SelectOptions[] = [
  { label: "Family", value: "family" },
  { label: "Friend", value: "friend" },
  { label: "GP", value: "gp" },
  { label: "Social worker", value: "social_worker" },
  { label: "Health professional", value: "health_professional" },
  { label: "Other", value: "other" },
];

export const JOB_ROLES_FOR_RESTRICTIONS: SelectOptions[] = [
  { value: "care_coordinator-#90BE6D", label: "Care Coordinator" },
  { value: "carer-#A7861E", label: "Carer" },
  { value: "carer_(driver)-#F9C74F", label: "Carer (Driver)" },
  { value: "support_worker-#2D9CDB", label: "Support Worker" },
  {
    value: "support_worker_(driver)-#7209B7",
    label: "Support Worker (Driver)",
  },
  { value: "house_keeping-#00A896", label: "House Keeping" },
  { value: "nurse-#9B5DE5", label: "Nurse" },
  { value: "kitchen-#FF9000", label: "Kitchen" },
  // { value: "other-#FFD700", label: "Other" },
];

export const JOB_ROLES: SelectOptions[] = [
  { value: "admin-#F94144", label: "Admin" },
  { value: "care_coordinator-#90BE6D", label: "Care Coordinator" },
  { value: "carer-#A7861E", label: "Carer" },
  { value: "carer_(driver)-#F9C74F", label: "Carer (Driver)" },
  { value: "support_worker-#2D9CDB", label: "Support Worker" },
  {
    value: "support_worker_(driver)-#7209B7",
    label: "Support Worker (Driver)",
  },
  { value: "management-#F15BB5", label: "Management" },
  { value: "house_keeping-#00A896", label: "House Keeping" },
  { value: "nurse-#9B5DE5", label: "Nurse" },
  { value: "kitchen-#FF9000", label: "Kitchen" },
  // { value: "other-#FFD700", label: "Other" },
];

export const BODY_TYPES: SelectOptions[] = [
  {
    value: "ectomorph",
    label: "Ectomorph",
  },
  {
    value: "mesomorph",
    label: "Mesomorph",
  },
  {
    value: "endomorph",
    label: "Endomorph",
  },
];

export const HOMEOWNER: SelectOptions[] = [
  {
    value: "homeowner",
    label: "Homeowner",
  },
  // {
  //   value: "tenant",
  //   label: "Tenant",
  // },
  {
    value: "renting",
    label: "Renting",
  },
  // {
  //   value: "landlord",
  //   label: "Landlord",
  // },
  // {
  //   value: "home_sharer",
  //   label: "Home Sharer",
  // },
  {
    value: "other",
    label: "Other",
  },
];

export const ETHNICITY: SelectOptions[] = [
  { label: "Asian or Asian British", value: "", isGroupTitle: true },
  { label: "Indian", value: "indian" },
  { label: "Pakistani", value: "pakistani" },
  { label: "Bangladeshi", value: "bangladeshi" },
  { label: "Chinese", value: "chinese" },
  { label: "Any other Asian background", value: "other_asian" },
  {
    label: "Black, Black British, Caribbean or African",
    value: "",
    isGroupTitle: true,
  },
  { label: "Caribbean", value: "caribbean" },
  { label: "African", value: "african" },
  {
    label: "Any other Black, Black British, or Caribbean background",
    value: "other_black",
  },
  { label: "Mixed or multiple ethnic groups", value: "", isGroupTitle: true },
  { label: "White and Black Caribbean", value: "white_black_caribbean" },
  { label: "White and Black African", value: "white_black_african" },
  { label: "White and Asian", value: "white_and_asian" },
  {
    label: "Any other Mixed or multiple ethnic background",
    value: "other_mixed",
  },
  { label: "White", value: "", isGroupTitle: true },
  {
    label: "English, Welsh, Scottish, Northern Irish or British",
    value: "british",
  },
  { label: "Irish", value: "irish" },
  { label: "Gypsy or Irish Traveller", value: "gypsy_irish_traveller" },
  { label: "Roma", value: "roma" },
  { label: "Any other White background", value: "other_white" },
  { label: "Other ethnic group", value: "", isGroupTitle: true },
  { label: "Arab", value: "arab" },
  { label: "Any other ethnic group", value: "other_ethnic_group" },
];

export const RELIGION: SelectOptions[] = [
  { label: "Buddhist", value: "buddhist" },
  { label: "Christian", value: "christian" },
  { label: "Hindu", value: "hindu" },
  { label: "Muslim", value: "muslim" },
  { label: "Jewish", value: "jewish" },
  { label: "Sikh", value: "sikh" },
  { label: "Non-religion", value: "non_religion" },
  { label: "Other", value: "other" },
];

export const SEXUAL_ORIENTATION: SelectOptions[] = [
  { label: "Straight or Heterosexual", value: "straight_or_heterosexual" },
  { label: "Gay or Lesbian", value: "gay_or_lesbian" },
  { label: "Bisexual", value: "bisexual" },
  { label: "Pansexual", value: "pansexual" },
  { label: "Asexual", value: "asexual" },
  { label: "Queer", value: "queer" },
  { label: "Other", value: "other" },
];

export const MARITAL_STATUS: SelectOptions[] = [
  { label: "Single", value: "single" },
  { label: "Married/Civil Partner", value: "married_civil_partner" },
  { label: "Divorced", value: "divorced" },
  { label: "Widowed", value: "widowed" },
  { label: "Separated", value: "separated" },
  { label: "Other", value: "other" },
];

export const EMPLOYMENT_TYPE: SelectOptions[] = [
  { label: "Full Time/Permanent Staff", value: "full_time/permanent_staff" },
  { label: "Part Time", value: "part_time" },
  { label: "Bank", value: "bank" },
  { label: "Agency Staff", value: "agency_staff" },
  { label: "Zero Hour Contract", value: "zero_hour_contract" },
  { label: "Other", value: "other" },
];

export const DEPARTMENTS: SelectOptions[] = [
  {
    value: "emergency_medicine",
    label: "Emergency Medicine",
  },
  {
    value: "cardiology",
    label: "Cardiology",
  },
  {
    value: "oncology",
    label: "Oncology",
  },
  {
    value: "pediatrics",
    label: "Pediatrics",
  },
  {
    value: "obstetrics_and_gynecology",
    label: "Obstetrics and Gynecology",
  },
  {
    value: "neurology",
    label: "Neurology",
  },
  {
    value: "orthopedics",
    label: "Orthopedics",
  },
  {
    value: "gastroenterology",
    label: "Gastroenterology",
  },
  {
    value: "dermatology",
    label: "Dermatology",
  },
  {
    value: "psychiatry",
    label: "Psychiatry",
  },
  {
    value: "radiology",
    label: "Radiology",
  },
  {
    value: "anesthesiology",
    label: "Anesthesiology",
  },
  {
    value: "ophthalmology",
    label: "Ophthalmology",
  },
  {
    value: "nephrology",
    label: "Nephrology",
  },
  {
    value: "urology",
    label: "Urology",
  },
  {
    value: "pulmonology",
    label: "Pulmonology",
  },
  {
    value: "otolaryngology",
    label: "Otolaryngology (ENT)",
  },
  {
    value: "rehabilitation",
    label: "Rehabilitation",
  },
  {
    value: "intensive_care_unit",
    label: "Intensive Care Unit (ICU)",
  },
  {
    value: "other",
    label: "Other",
  },
];

export const POSTAL_CODE: SelectOptions[] = [
  {
    value: "----",
    label: "Le5 4lr",
  },
];
export const SERVICE_USER_STATUS: SelectOptions[] = [
  {
    value: "  ",
    label: "",
  },
];
export const SERVICE_TYPE: SelectOptions[] = [
  {
    value: " -----",
    label: "------",
  },
  {
    value: " -----",
    label: "------",
  },
  {
    value: " -----",
    label: "------",
  },
];

export const REGEX = {
  name: /^[a-zA-Z\s]*$/,
  email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  number: /^[0-9]*$/,
  password:
    /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/,
  // password: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*()]).*$/,
};

export const INITIAL_STAFF_DATA = {
  preferredName: "",
  title: "",
  avatar: "",
  firstName: "",
  middleName: "",
  lastName: "",
  gender: "",
  dateOfBirth: "",
  age: "",
  status: "",
  jobRoles: "",
  address: {
    buildingName: "",
    addressLine1: "",
    addressLine2: "",
    town: "",
    country: "",
    postCode: "",
  },
  postCode: "",
  email: "",
  landlineNumber: "",
  mobileNumber: "",
  phoneNumber: "",
  accessLevel: "",
  password: "",
  pin: "",
  ethnicity: "",
  religion: "",
  sexualOrientation: "",
  maritalStatus: "",
  startDate: "",
  DBSNumber: "",
  employmentType: "",
  departments: "",
  contractedHours: "",
  contacts: [] as IContacts[],
  financeDetails: {
    bankName: "",
    accountName: "",
    accountNumber: "",
    sortCode: "",
    niNumber: "",
    payrollNumber: "",
  },
};

export const INITIAL_SIGNUP_DATA = {
  firstName: "",
  lastName: "",
  companyName: "",
  email: "",
  serviceType: "",
  jobTitle: "",
  phone: "",
  activeServiceUsers: "",
  noOfDifferentSites: "",
  termsAndConditions: false,
};

export const CONTACT_DATA = {
  firstName: "",
  lastName: "",
  middleName: "",
  orgName: "",
  contactType: "",
  age: "",

  relationship: "",
  email: "",
  phoneNumber: "",
  address: {
    buildingName: "",
    addressLine1: "",
    addressLine2: "",
    town: "",
    country: "",
    postCode: "",
  },
  postCode: "",
  note: "",
};

export const INITIAL_SERVICE_USER_DATA = {
  preferredName: "",
  title: "",
  avatar: "",
  firstName: "",
  middleName: "",
  lastName: "",
  gender: "",
  dateOfBirth: "",
  age: "",
  zone: "",
  address: {
    buildingName: "",
    addressLine1: "",
    addressLine2: "",
    town: "",
    country: "",
    postCode: "",
  },
  email: "",

  postCode: "",
  landlineNumber: "",
  telephoneNumber: "",
  keySafeNumber: "",
  assignedSite: "",
  assignedFloor: "",
  assignedRoom: "",
  assignedBed: "",
  niNumber: "",
  nhsNumber: "",
  socialServiceRef: "",
  assignedCarers: [],
  // personal appearance
  weight: "",
  height: "",
  eyeColor: "",
  bodyType: "",
  hairColor: "",
  glassWare: "",
  hearingAid: "",
  // contact details
  contacts: [] as IContacts[],
  // invoice contacts
  invoiceContacts: [] as Array<{
    title: string;
    name: string;
    email: string;
    isActive: boolean;
  }>,
  // additional details
  homeOwner: "",
  ethnicity: "",
  religion: "",
  sexualOrientation: "",
  maritalStatus: "",
  joiningDate: "",
  fundingSource: "",
  invoiceSettings: {
    defaultDailyRate: "",
    weekendHolidayRate: "",
  },
  // carer alerts
  carerAlerts: {
    DNCPR: {},
    LPS: {},
    DoLS: {},
    Behaviour: {},
    COPD: {},
    Allergies: {},
    Diabetes: {},
    Falls: {},
    Epilepsy: {},
    Dementia: {},
    Autism: {},
    ChokingRisk: {},
    MentalHealth: {},
    SelfNeglect: {},
  },

  isGPSCheckInActive: false,
  addressCoordinates: {
    lat: null,
    lng: null,
  },
  checkInRadius: 250,
};

export const GRAPH_DATA: ApexChartOptions = {
  chart: {
    type: "pie",
    width: 250,
  },
  colors: ["#F94144", "#90BE6D", "#F8961E", "#F9C74F", "#2D9CDB", "#F3722C"],
  labels: [],
  series: [],
  noData: {
    text: "Loading...",
  },
  legend: {
    position: "left",
  },
  plotOptions: {
    pie: {
      donut: {
        size: "100%",
      },
    },
    labels: false,
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        chart: {
          width: 150,
        },
        legend: {
          position: "top",
          color: "red",
        },
      },
    },
  ],
};

export const STAFF_PERSONAL_INFO_REQ_INPUTS = [
  "firstName",
  "lastName",
  "gender",
  "dateOfBirth",
  "age",
  "status",
  "jobRoles",
  "email",
  "password",
];

export const STAFF_ADD_INFO_REQ_INPUTS = ["startDate", "address"];

export const SERVICE_PERSONAL_INFO_REQ_INPUTS = [
  "firstName",
  "lastName",
  "gender",
  "dateOfBirth",
  "age",
  "status",
];

export const SERVICE_ADD_INFO_REQ_INPUTS = ["joiningDate", "fundingSource"];

export const SERVICE_ADDRESS_INPUTS = ["postCode", "addressCoordinates"];

export const DUMMY_CONDITIONS = [
  {
    id: "1",
    title: "Abrasion",
  },
  {
    id: "2",
    title: "Blister",
  },
  {
    id: "3",
    title: "Bruise",
  },
  {
    id: "4",
    title: "Burn/ Scald",
  },
  {
    id: "5",
    title: "Cut",
  },
  {
    id: "6",
    title: "Moisture Lesion",
  },
  {
    id: "7",
    title: "Pressure Sore",
  },
  {
    id: "8",
    title: "Red Area",
  },
  {
    id: "9",
    title: "Swelling",
  },

  {
    id: "10",
    title: "Scratch",
  },
  {
    id: "11",
    title: "Skin Tear",
  },
  {
    id: "12",
    title: "Other injury/mark",
  },
  {
    id: "13",
    title: "Rash",
  },
  {
    id: "14",
    title: "Discoloration",
  },
  {
    id: "15",
    title: "Incision",
  },
  {
    id: "16",
    title: "Redness",
  },
  {
    id: "17",
    title: "Other",
  },
];

export const INITIAL_BODYNOTE_COMMENT = {
  bodyMapNote: {
    label: "",
    value: "",
  },
  description: "",
  injuryCondition: "",
  pinComment: "",
  pinnedStaff: [],
};

export const medicationTypeLabel: Record<TypeMedication, string> = {
  [TypeMedication.SCHEDULED]: "Scheduled",
  [TypeMedication.PRN]: "PRN",
  [TypeMedication.BLISTER_PACK]: "Blister Pack",
};

export const STANDARD_TEMPLATE = [
  {
    id: "template-current-strengths",
    type: QuestionType.Text,
    questionText: "Current Strengths",
    subheading:
      "Used for: what the person can already do; established abilities.",
    comment: "",
    answer: "",
    options: [],
    subQuestions: [],
  },
  {
    id: "template-support-requirements",
    type: QuestionType.Text,
    questionText: "Support Requirements",
    subheading:
      "Used for: what help, assistance, or interventions are required.",
    comment: "",
    answer: "",
    options: [],
    subQuestions: [],
  },
  {
    id: "template-preferences",
    type: QuestionType.Text,
    questionText: "Preferences",
    subheading:
      "Used for: the individual’s likes, choices, and ways they prefer to receive support.",
    comment: "",
    answer: "",
    options: [],
    subQuestions: [],
  },
  {
    id: "template-monitoring-requirements",
    type: QuestionType.Text,
    questionText: "Monitoring Requirements",
    subheading:
      "Used for: what observations, checks, or assessments are needed to ensure safety and wellbeing.",
    comment: "",
    answer: "",
    options: [],
    subQuestions: [],
  },
  {
    id: "template-planned-outcomes",
    type: QuestionType.Text,
    questionText: "Planned Outcomes",
    subheading: "Used for: goals, targets, or expected results.",
    comment: "",
    answer: "",
    options: [],
    subQuestions: [],
  },
];

export const MessageType = {
  CONCERNS_COMPLAINTS: "Concerns & Complaints",
  GENERAL: "General",
  ROTA_UPDATE: "Rota Update",
  TRAINING: "Training",
  COMPLIMENTS_RECOGNITION: "Compliments & Recognition",
  OFFICE_UPDATES: "Office Updates",
  OTHER: "Other",
};

export const ALERT_PRIORITY = {
  LOW: "Low",
  MEDIUM: "Medium",
  HIGH: "High",
};

export const ALERT_STATUS = {
  ACTION_NEEDED: "Action Needed",
  IN_PROGRESS: "InProgress",
  RESOLVED: "Resolved",
  DISMISSED: "Dismissed",
};

export const ALERT_REFERENCE = {
  SCHEDULE_VISIT: {
    label: "Schedule Visit",
    value: "ScheduleVisit",
  },
  MEDICATION: {
    label: "Medication",
    value: "medication",
  },
  DAILY_NOTES: {
    label: "Daily Notes",
    value: "dailyNotes",
  },
  BODY_MAP: {
    label: "Body Map",
    value: "BodyMapNote",
  },
};

export const ALERT_CATEGORY = {
  SCHEDULE_VISIT_ALERT: {
    FORCED_CHECK_IN: {
      label: "Location Mismatch Check In",
      value: "forcedCheckIn",
    },
    FORCED_CHECK_OUT: {
      label: "Location Mismatch Check Out",
      value: "forcedCheckOut",
    },
    NOT_CHECKED_OUT_AFTER_72_HOURS: {
      label: "Not Checked Out",
      value: "notCheckedOutAfter72Hours",
    },
    SYSTEM_AUTO_CHECK_OUT: {
      label: " Auto Check-Out",
      value: "systemAutoCheckOut",
    },
    LATE_CHECK_IN: {
      label: "Late Check-In",
      value: "lateCheckIn",
    },
    EARLY_CHECK_IN: {
      label: "Early Check-In",
      value: "earlyCheckIn",
    },
    CARE_PROFESSIONAL_NOT_CHECKED_IN: {
      label: "Missed Visit",
      value: "careProfessionalNotCheckedIn",
    },
  },
  MEDICATION_ALERT: MEDICATION_OUTCOMES.reduce((acc, item) => {
    if (item?.value === "FT") {
      return acc;
    }

    const key = item.label.toUpperCase().replace(/\s+/g, "_");

    acc[key] = {
      label: item.label,
      value: item.value,
    };

    return acc;
  }, {}),
};

export const ALERT_CATEGORY_LABELS = Object.values(ALERT_CATEGORY)
  .flatMap((group) => Object.values(group as Record<string, AlertCategory>))
  .reduce<Record<string, string>>((acc, { value, label }) => {
    acc[value] = label;
    return acc;
  }, {});
